import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import LeaveDialog from './LeaveDialog.js';
import { AppArea } from './WorkspaceErrorBoundary.js';
export { draftsDiffer, normalizedDraftSignature } from './draftState.js';

const UnsavedChangesContext = createContext(null);

// Route transitions need a synchronous answer before React can unmount the
// current editor. All other decisions use the app-owned accessible dialog.
export function confirmUnsavedNavigation(message) {
  const confirm = typeof window === 'undefined' ? null : window['confirm'];
  return typeof confirm === 'function' ? confirm(message) : true;
}

// The words of the leave dialog: a count only when every dirty editor
// reports one (from its change list); never an invented number.
export function leaveDialogCopy(dirtyEditors = [], pageLabel = '') {
  const labels = [...new Set(dirtyEditors.map(editor => editor.label).filter(Boolean))].join(', ') || 'this page';
  const counted = dirtyEditors.length > 0 && dirtyEditors.every(editor => Number.isSafeInteger(editor.changes) && editor.changes > 0);
  const total = counted ? dirtyEditors.reduce((sum, editor) => sum + editor.changes, 0) : 0;
  return {
    title:`Leave ${pageLabel || 'this page'}?`,
    body:counted
      ? `You have ${total} unsaved ${total === 1 ? 'change' : 'changes'} in ${labels}. Leaving discards them.`
      : `You have unsaved changes in ${labels}. Leaving discards them.`,
  };
}

// Runs `run` once a leave answer allows it: the shell's guard answers true
// at once when nothing is unsaved, and a promise while the dialog is open.
export function afterLeaveAllowed(answer, run) {
  if (answer === false) return;
  if (answer && typeof answer.then === 'function') {
    answer.then(allowed => { if (allowed) run(); }, () => {});
    return;
  }
  run();
}

export function UnsavedChangesProvider({ children }) {
  const [editors, setEditors] = useState({});
  const [leaveRequest, setLeaveRequest] = useState(null);

  const register = useCallback((id, definition) => {
    if (!id) return () => {};
    const changes = Number.isSafeInteger(definition?.changes) && definition.changes > 0 ? definition.changes : null;
    setEditors(current => ({ ...current, [id]:{ id, label:definition?.label || 'this editor', dirty:Boolean(definition?.dirty), changes } }));
    return () => setEditors(current => {
      if (!Object.prototype.hasOwnProperty.call(current, id)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const dirtyEditors = useMemo(() => Object.values(editors).filter(editor => editor.dirty), [editors]);
  const dirtyRef = useRef(dirtyEditors);
  dirtyRef.current = dirtyEditors;
  const confirmNavigation = useCallback((message = '') => {
    if (!dirtyEditors.length) return true;
    const labels = dirtyEditors.map(editor => editor.label).join(', ');
    return confirmUnsavedNavigation(message || `Discard unsaved changes in ${labels}?`);
  }, [dirtyEditors]);
  // The shell's way out (rail, section menu, breadcrumb, search, a
  // notification, a workspace, brand or team switch): one app dialog for
  // every dirty editor together. True at once when nothing is unsaved;
  // otherwise a promise of the answer. Browser back and page-level callers
  // keep the synchronous confirmNavigation above.
  const requestLeave = useCallback(({ pageLabel = '' } = {}) => {
    const dirty = dirtyRef.current;
    if (!dirty.length) return true;
    return new Promise(resolve => {
      setLeaveRequest(current => {
        current?.resolve(false);
        return { pageLabel, editors:dirty, resolve };
      });
    });
  }, []);
  const answerLeave = useCallback(allowed => {
    setLeaveRequest(current => {
      current?.resolve(allowed);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!dirtyEditors.length) return undefined;
    const beforeUnload = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirtyEditors.length]);

  const value = useMemo(() => ({
    register,
    confirmNavigation,
    requestLeave,
    hasUnsavedChanges:dirtyEditors.length > 0,
    dirtyEditors,
  }), [confirmNavigation, dirtyEditors, register, requestLeave]);
  return <UnsavedChangesContext.Provider value={value}>
    {children}
    {/* FE-01: if the question fails, its notice keeps the changes; closing it stays on the page. App (a sibling here) stays. */}
    {leaveRequest && <AppArea area="leave" onClose={() => answerLeave(false)}><LeaveQuestion request={leaveRequest} onAnswer={answerLeave}/></AppArea>}
  </UnsavedChangesContext.Provider>;
}

// The question is built inside its boundary, so a failure in its wording is caught too.
function LeaveQuestion({ request, onAnswer }) {
  return <LeaveDialog {...leaveDialogCopy(request.editors, request.pageLabel)} onStay={() => onAnswer(false)} onLeave={() => onAnswer(true)}/>;
}

export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext) || {
    register:() => () => {},
    confirmNavigation:() => true,
    requestLeave:() => true,
    hasUnsavedChanges:false,
    dirtyEditors:[],
  };
}

// `changes` (optional): how many changes the editor's change list holds, so
// the leave dialog can say 'You have N unsaved changes'. Pass it only where
// the editor already counts its changes.
export function useUnsavedChangesGuard({ id, label, dirty, changes = null }) {
  const context = useUnsavedChanges();
  const dirtyRef = useRef(Boolean(dirty));
  const lastIntentRef = useRef({ at:0, target:null, active:false });
  // A user edit stays "recent" for 1.5s of event-loop time. The expiry is a
  // timer rather than a wall-clock comparison so a busy or slow device (where
  // React's effect runs late) does not misread a real edit as programmatic.
  const intentExpiryRef = useRef(null);
  // Counts every recorded intent. An effect compares it with the value seen at
  // render time to tell "edited after this render" from a stale intent.
  const intentSeqRef = useRef(0);
  const intentSeqAtRender = intentSeqRef.current;
  const intentTimerRef = useRef(null);
  const intentRevisionRef = useRef(0);
  const [deliberate, setDeliberate] = useState(false);
  dirtyRef.current = Boolean(dirty);

  useEffect(() => {
    let active = true;
    // A new editor identity cannot inherit another editor's input intent.
    lastIntentRef.current = { at:0, target:null, active:false };
    setDeliberate(false);
    const recordIntent = event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[data-draft-ignore="true"], nav, [role="tab"], [role="menu"], [role="option"]')) return;
      const control = target.closest('input, textarea, select, [contenteditable="true"], [role="switch"], [data-draft-mutation="true"], button');
      if (!control) return;
      if (control.matches('button')) {
        const labelText = `${control.getAttribute('aria-label') || ''} ${control.textContent || ''}`.trim().toLowerCase();
        if (/^(close|back|cancel|refresh|reload|open|view|preview|search|filter|previous|next|load more)\b/.test(labelText)) return;
      }
      intentSeqRef.current += 1;
      lastIntentRef.current = { at:Date.now(), target:control, active:true };
      // The expiry is armed after React commits the edit (see the layout effect).
      if (intentExpiryRef.current !== null) clearTimeout(intentExpiryRef.current);
      intentExpiryRef.current = null;
      if (dirtyRef.current) {
        // Native capture runs before React reads a controlled input's value.
        // Updating React state here can restore the old value before onChange
        // consumes it. Keep the intent immediately, but notify after the whole
        // native event (not a microtask between listeners) has completed.
        if (intentTimerRef.current !== null) clearTimeout(intentTimerRef.current);
        const revision = ++intentRevisionRef.current;
        intentTimerRef.current = setTimeout(() => {
          intentTimerRef.current = null;
          if (active && revision === intentRevisionRef.current && dirtyRef.current) setDeliberate(true);
        }, 0);
      }
    };
    document.addEventListener('input', recordIntent, true);
    document.addEventListener('change', recordIntent, true);
    document.addEventListener('click', recordIntent, true);
    return () => {
      active = false;
      intentRevisionRef.current += 1;
      if (intentTimerRef.current !== null) clearTimeout(intentTimerRef.current);
      intentTimerRef.current = null;
      if (intentExpiryRef.current !== null) clearTimeout(intentExpiryRef.current);
      intentExpiryRef.current = null;
      document.removeEventListener('input', recordIntent, true);
      document.removeEventListener('change', recordIntent, true);
      document.removeEventListener('click', recordIntent, true);
    };
  }, [id]);

  // Arm the 1.5s expiry only once the edit has been rendered, so a slow render
  // cannot expire the intent before the dirty check below runs.
  useLayoutEffect(() => {
    if (!lastIntentRef.current.active || intentExpiryRef.current !== null) return;
    intentExpiryRef.current = setTimeout(() => {
      intentExpiryRef.current = null;
      lastIntentRef.current = { ...lastIntentRef.current, active:false };
    }, 1500);
  });

  useEffect(() => {
    if (!dirty) {
      intentRevisionRef.current += 1;
      if (intentTimerRef.current !== null) clearTimeout(intentTimerRef.current);
      intentTimerRef.current = null;
      setDeliberate(false);
      // On a busy device this effect can run after the user's next edit has
      // already been recorded; that newer intent must survive.
      if (intentSeqRef.current === intentSeqAtRender) lastIntentRef.current = { at:0, target:null, active:false };
      return;
    }
    if (lastIntentRef.current.active || Date.now() - lastIntentRef.current.at <= 1500) setDeliberate(true);
  }, [dirty]);

  useEffect(() => context.register(id, { label, dirty:Boolean(dirty && deliberate), changes }), [context.register, changes, deliberate, dirty, id, label]);
  return context;
}
